import {FormattedMessage} from "react-intl";
import {useOutletContext} from "react-router-dom";

import HelmetSEO from "../../components/helmetSeo/helmetSeo";
import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/footer/footer";
import SBEditable from "../../components/sbEditable/sbEditable";
import ArticleDetail from "../../components/articleDetail/articleDetail";
import ArticleThumbnail from "../../components/ArticleThumbnail/ArticleThumbnail";
import RichTextWithBlokRenderer from "../../components/richTextRenderer/richTextRenderer";
import RouterLink from "../../components/link/RouterLink";
import Tag, {TagStoryTag} from "../../components/tag/tag";

import GetComponentsFromBloks from "../../helpers/bloksRenderer";
import {ArticleStoryToObject} from "../../helpers/storyHelpers";
import {articleUrlFromSlug, buildInternalPath} from "../../helpers/URLHelpers";

import styles from "./article.module.scss";
import {useGuestbook} from "../../features/comments/hooks/useGuestbook";
import CommentingSection from "../../features/comments/components/CommentingSection";
import ArticleSchema from "../../components/ArticleSchema";
import GuestbookContext from "../../features/comments/context/GuestbookContext";
import PageContentWrapper from "../../components/PageContentWrapper";
import {ArticleCmsRecommendedStreaming} from "../../bloks/asidewidgets/articleCmsRecommendedStreaming/articleCmsRecommendedStreaming";
import RssFeedLink from "../../features/seo/rssFeedLink";

function ArticleLayout() {
  const {story} = useOutletContext();

  const article = story ? ArticleStoryToObject(story) : {};
  const articleUrl = articleUrlFromSlug(article.slug);

  let componentList = [];
  if (story && story.content && story.content.body) {
    componentList = GetComponentsFromBloks(story.content.body, story);
  }
  let asideComponentList = [];
  if (story && story.content && story.content.aside) {
    asideComponentList = GetComponentsFromBloks(story.content.aside, story, {
      category: article?.category?.content?.category || "",
    });
  }

  const [totalComments, guestbookKey, increaseTotalComments] = useGuestbook(
    article.legacyid || article.uuid
  );

  return (
    <>
      <HelmetSEO
        initialAlternates={[]}
        lang={story.lang}
        seo_data={article ? article.seo : {}}
        page_path={articleUrl}
        story_alternates={article ? article.alternates : []}
        fallback_title={article ? article.headline : ""}
        fallback_description={article ? article.lead : ""}
        fallback_image_url={
          article && article.thumbnail && article.thumbnail.filename
            ? article.thumbnail.filename
            : ""
        }
      />
      <RssFeedLink />
      <ArticleSchema
        slug={article.slug}
        headline={article.headline}
        description={article.seo.description}
        articleDate={article.article_date}
        articleType={article.article_type?.content.article_type}
        thumbnail={article.thumbnail.filename}
        authors={article.authors}
      />
      <Navbar story_language={story.lang} />
      <PageContentWrapper>
        <div className={styles.view}>
          <div className={styles.mainlayout}>
            <main className={styles.mainpage}>
              {article ? (
                <SBEditable blok={article}>
                  <ArticleDetail
                    key={article.uuid}
                    headline={article.headline}
                    lead={article.lead}
                    authors={article.authors}
                    category={article.category}
                    article_type={article.article_type}
                    date={article.article_date}
                    enable_comments={article.has_comments}
                    comment_count={totalComments}
                    enable_social_sharing={article.has_social_sharing}
                  />
                </SBEditable>
              ) : null}
              {article.hide_thumbnail_image ? null : (
                <ArticleThumbnail image={article.thumbnail} />
              )}
              <div
                className={
                  article.legacy_paragraph_spacing
                    ? "legacy-paragraph-spacing"
                    : ""
                }
              >
                {componentList}
              </div>
              <div>
                {article.footnote ? (
                  <RichTextWithBlokRenderer document={article.footnote} />
                ) : null}
                {article.has_social_sharing && article.has_social_sharing ? (
                  <button
                    className={`limit-visibility ${styles.lowerShareBtn}`}
                  >
                    <FormattedMessage id="page.article.share" />
                  </button>
                ) : null}
                <div className={styles.tagRow}>
                  {article.category ? (
                    <RouterLink to={buildInternalPath(article.category.slug)}>
                      <Tag label={article.category.content.title} />
                    </RouterLink>
                  ) : null}
                  {article.article_type ? (
                    <RouterLink
                      to={buildInternalPath(article.article_type.slug)}
                    >
                      <Tag label={article.article_type.content.title} />
                    </RouterLink>
                  ) : null}
                  {story.tag_list.map(tag => (
                    <TagStoryTag key={tag} tag_name={tag} />
                  ))}
                </div>
                <hr className={styles.endingHr} />
              </div>
              <div>
                <GuestbookContext.Provider
                  value={{totalComments, guestbookKey, increaseTotalComments}}
                >
                  <CommentingSection />
                </GuestbookContext.Provider>
              </div>
            </main>
            <aside className={styles.asidepage}>
              <div className={styles.asidecontent}>
                {asideComponentList}
                <ArticleCmsRecommendedStreaming key="cms-recommnded-streaming" />
              </div>
            </aside>
          </div>
        </div>
      </PageContentWrapper>
      <Footer />
    </>
  );
}

export default ArticleLayout;
